/**
 * Skill Score Stepper
 *
 * Mobile-friendly decimal input for the 18-skill technical development
 * scores. Combines:
 *   - − / + buttons that step by 0.1 (large hit targets for mobile)
 *   - free-typing decimal input with locale tolerance (comma OR dot)
 *   - select-all on focus so a single tap replaces the value instead of
 *     appending to the existing digits
 *   - clamp + round only on blur, never mid-typing
 *
 * The committed value is always a Number passed through `onCommit(skillKey,
 * value)`. Mid-typing strings are kept local — the parent state stays
 * numeric and consistent.
 */
import { useEffect, useRef, useState } from 'react';

const clampRound = (n, min, max) => {
  if (!Number.isFinite(n)) return null;
  const clamped = Math.max(min, Math.min(max, n));
  return Math.round(clamped * 10) / 10;
};

const SkillScoreStepper = ({ skillKey, value, min, max, rangeLabel, onCommit, disabled = false, dataTestId }) => {
  const inputRef = useRef(null);
  const focusedRef = useRef(false);
  // Local draft string used while the user is typing. The parent owns the
  // numeric value; we resync from the prop whenever the input is NOT focused.
  const [draft, setDraft] = useState(() => (value == null ? '' : Number(value).toFixed(1)));

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value == null || value === '' ? '' : Number(value).toFixed(1));
    }
  }, [value]);

  const commit = (n) => {
    const cr = clampRound(n, min, max);
    if (cr == null) return;
    onCommit(skillKey, cr);
    setDraft(cr.toFixed(1));
  };

  const step = (delta) => {
    const raw = parseFloat(draft);
    const base = Number.isFinite(raw) ? raw : (Number(value) || min);
    commit(base + delta);
  };

  return (
    <div className="flex items-center gap-1.5 bg-muted/50 px-1.5 py-1 rounded">
      <button
        type="button"
        disabled={disabled}
        onClick={() => step(-0.1)}
        className="h-7 w-7 rounded border border-border/50 bg-background hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold text-base leading-none active:scale-95 transition-transform shrink-0"
        aria-label={`Decrease ${skillKey} score by 0.1`}
        data-testid={`${dataTestId}-minus`}
      >
        −
      </button>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        value={draft}
        disabled={disabled}
        onFocus={(e) => {
          focusedRef.current = true;
          try { e.target.select(); } catch { /* noop */ }
        }}
        onChange={(e) => {
          // Free typing: allow digits + a single separator (dot or comma).
          // Do NOT clamp / round / parse here — that happens on blur or step.
          let v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
          const parts = v.split('.');
          if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
          setDraft(v);
        }}
        onBlur={() => {
          focusedRef.current = false;
          const v = draft.trim();
          if (v === '' || v === '.') {
            // Empty / partial — revert to the last committed value.
            setDraft(value == null ? '' : Number(value).toFixed(1));
            return;
          }
          const n = parseFloat(v);
          if (Number.isNaN(n)) {
            setDraft(value == null ? '' : Number(value).toFixed(1));
            return;
          }
          commit(n);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        className="w-14 h-7 text-sm text-center bg-transparent border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-primary tabular-nums"
        data-testid={dataTestId}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => step(0.1)}
        className="h-7 w-7 rounded border border-border/50 bg-background hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold text-base leading-none active:scale-95 transition-transform shrink-0"
        aria-label={`Increase ${skillKey} score by 0.1`}
        data-testid={`${dataTestId}-plus`}
      >
        +
      </button>
      {rangeLabel && (
        <span className="text-[10px] text-muted-foreground ml-1 hidden sm:inline">({rangeLabel})</span>
      )}
    </div>
  );
};

export default SkillScoreStepper;
